import { millisecondsToSeconds, minutesToSeconds, secondsToMilliseconds } from 'date-fns';
import { secondsInDay } from 'date-fns/constants';
import { action, computed, flow, makeObservable, observable, runInAction } from 'mobx';

import { agent } from 'src/api/agent';
import type { ViewSettings } from 'src/api/chart/drilling-plan-charts-api';
import { hasValue } from 'src/shared/utils/common';
import { downloadFile } from 'src/shared/utils/download-file';
import { PlanVersionType } from 'src/store/comparison/types';
import type { ExportService } from 'src/store/export-service';
import { EXPORT_TAGS } from 'src/store/export-service/consts';
import type { TExportData } from 'src/store/export-service/export-service';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import type { PlanVersionsService } from 'src/store/plan-version/plan-versions-service';
import {
  GOPLAN_DRILLING_CARPET_EXPORT_GANTT,
  GOPLAN_DRILLING_CARPET_EXPORT_PREPARATION,
  GOPLAN_DRILLING_CARPET_EXPORT_WELL_LIST,
} from 'src/store/roles-store/roles';
import type { RolesStore } from 'src/store/roles-store/roles-store';

type TExportModalData = {
  startDate: number;
  endDate: number | null;
  wellList: boolean;
  gantt: boolean;
  wellPadPreparations: boolean;
};

const DEFAULT_MODAL_DATA_WITHOUT_START_DATE = {
  endDate: null,
  wellList: false,
  gantt: false,
  wellPadPreparations: false,
};

// DatePicker2V сейчас при выборе даты возвращает выбранную дату, но текущее время пользователя. Эта утилка сбрасывает время до 00:00:00
export const fixDatePickerTime = (dateInSec: number): number => {
  return Math.floor(dateInSec / secondsInDay) * secondsInDay;
};

export class ExportStore {
  private readonly exportService: ExportService;
  private readonly planVersionService: PlanVersionsService;
  private readonly rolesStore: RolesStore;
  private readonly notifications: NotificationsStore;
  @observable viewData: TExportModalData;
  @observable isModalOpen = false;
  @observable isPaletteSettingsLoading = false;
  @observable private graphPaletteSettings: ViewSettings.RigsChartRawSettingsValues | null = null;

  constructor(
    exportService: ExportService,
    planVersionService: PlanVersionsService,
    notifications: NotificationsStore,
    rolesStore: RolesStore,
  ) {
    this.exportService = exportService;
    this.planVersionService = planVersionService;
    this.notifications = notifications;
    this.rolesStore = rolesStore;
    const startDate = fixDatePickerTime(millisecondsToSeconds(Date.now()));

    this.viewData = {
      startDate,
      ...DEFAULT_MODAL_DATA_WITHOUT_START_DATE,
    };

    makeObservable(this);
  }

  private getFixedDateWithTimezoneOffset(dateInUTC: number): number {
    const dateInst = new Date(secondsToMilliseconds(dateInUTC));
    const timezoneOffsetInSec = dateInst.getTimezoneOffset() * 60;

    return fixDatePickerTime(dateInUTC) + timezoneOffsetInSec;
  }

  private serializeViewDataToExportData(): TExportData {
    const _data = { ...this.viewData };

    _data.startDate = this.getFixedDateWithTimezoneOffset(_data.startDate);

    if (hasValue(_data.endDate)) {
      _data.endDate = this.getFixedDateWithTimezoneOffset(_data.endDate);
    }

    const { startDate, endDate } = _data;

    const exportTags = ExportStore.transformViewDataTagsToExportDataTags(_data);

    return {
      startDate,
      endDate,
      exportTags,
    };
  }

  private validate(): boolean {
    const isTagsChosen = (() => {
      const { wellList, gantt, wellPadPreparations } = this.viewData;
      return wellList || gantt || wellPadPreparations;
    })();

    if (!isTagsChosen) {
      this.notifications.showErrorMessageT('drillingCarpet:Export.selectAtLeastOneOption');

      return false;
    }

    return true;
  }

  private getShownItemFieldId(item: ViewSettings.SettingsControlValue): string {
    switch (item.fieldId) {
      case 'cluster':
        return 'Common_Cluster';
      case 'blockId':
        return 'Common_Block';
      case 'fieldId':
        return 'Common_Field';
      case 'subsoilUser':
        return 'Common_Organization';
      case 'wellPurposeId':
        return 'Common_WellPurpose';
      case 'fluidTypeId':
        return 'Common_FluidType';
      default:
        return item.fieldId;
    }
  }

  @computed
  get currentPaletteObjectType(): string {
    return (
      (
        this.graphPaletteSettings?.info?.['colorTab']?.settings as ViewSettings.SettingsControlValue[] | undefined
      )?.reduce((acc: string | null, shownItem) => {
        if (shownItem.isShown) {
          return this.getShownItemFieldId(shownItem);
        }

        return acc;
      }, null) ?? 'Common_Cluster'
    );
  }

  @computed
  get isWellListAvailable(): boolean {
    return this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_EXPORT_WELL_LIST]);
  }

  @computed
  get isGanttAvailable(): boolean {
    return this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_EXPORT_GANTT]);
  }

  @computed
  get isWPPAvailable(): boolean {
    const { currentVersion } = this.planVersionService;

    if (!this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_EXPORT_PREPARATION])) {
      return false;
    }

    if (!currentVersion) {
      console.error('cannot find current planVersion');
      return false;
    }

    const versionType = currentVersion.data.versionType;
    return versionType === PlanVersionType.public || versionType === PlanVersionType.current;
  }

  @computed
  get isExporting(): boolean {
    return this.exportService.isExporting;
  }

  @computed
  get file(): File | null {
    return this.exportService.file;
  }

  @computed
  get error(): string | null {
    return this.exportService.error;
  }

  @action.bound
  updateData(data: Partial<TExportModalData>): void {
    const _data = { ...data };

    if (hasValue(data.startDate)) {
      const dateInst = new Date(data.startDate * 1000);
      const timezoneOffset = dateInst.getTimezoneOffset();

      const fixedDate = fixDatePickerTime(data.startDate - minutesToSeconds(timezoneOffset));

      _data.startDate = fixDatePickerTime(fixedDate);
    }

    if (hasValue(data.endDate)) {
      const dateInst = new Date(data.endDate * 1000);
      const timezoneOffset = dateInst.getTimezoneOffset();

      const fixedDate = fixDatePickerTime(data.endDate - minutesToSeconds(timezoneOffset));

      _data.endDate = fixDatePickerTime(fixedDate);
    }

    this.viewData = { ...this.viewData, ..._data };
  }

  @action.bound
  setIsModalOpen(isOpen: boolean): void {
    this.isModalOpen = isOpen;

    if (isOpen) {
      this.loadGraphPaletteSettings();
    }
  }

  export = async (): Promise<void> => {
    if (!this.validate()) {
      return;
    }

    const serializedData = this.serializeViewDataToExportData();

    this.setIsModalOpen(false);

    await this.exportService.exportRequest({
      ...serializedData,
      ganttPaletteSections: [this.currentPaletteObjectType],
    });

    if (this.exportService.file) {
      downloadFile(this.exportService.file);
      this.exportService.removeFile();
    }
  };

  @flow.bound
  private async *loadGraphPaletteSettings() {
    this.isPaletteSettingsLoading = true;

    try {
      const { data: userSettingsObject } =
        await agent.get<ViewSettings.RigsChartRawSettingsValues>('userSettings/rigsGraph');
      yield;

      this.graphPaletteSettings = userSettingsObject;
    } catch (e) {
      console.error(e);
    } finally {
      this.isPaletteSettingsLoading = false;
    }
  }

  cancelImport = () => {
    this.exportService.cancelImport();
    this.resetModalData();
  };

  resetModalData = (): void => {
    setTimeout(() => {
      runInAction(() => {
        const startDate = fixDatePickerTime(millisecondsToSeconds(Date.now()));
        this.viewData = { startDate, ...DEFAULT_MODAL_DATA_WITHOUT_START_DATE };
      });
    }, 300);
  };

  init = () => {
    this.loadGraphPaletteSettings();
  };

  private static transformViewDataTagsToExportDataTags(
    data: Omit<TExportModalData, 'startDate' | 'endDate'>,
  ): EXPORT_TAGS[] {
    const tags: EXPORT_TAGS[] = [];

    if (data.wellList) {
      tags.push(EXPORT_TAGS.wellList);
    }

    if (data.gantt) {
      tags.push(EXPORT_TAGS.gantt);
    }

    if (data.wellPadPreparations) {
      tags.push(EXPORT_TAGS.wellPadPreparations);
    }

    return tags;
  }
}
